/**
 * Action Board — every role's next steps on one screen, worst first.
 *
 * The mockup's own section, and the reason it exists is the reason the radar exists: a lead does
 * not open this tab to admire the lights, they open it to find out what this week's work is. The
 * role cards below answer that one role at a time; this answers it for the portfolio.
 *
 * ⚠️ RAMPING ROLES ARE LEFT OFF, which is the mockup's rule and worth keeping. A role open two
 * weeks generates actions off signals that have not settled, and a board headed by three brand-new
 * roles is a board nobody trusts twice. Their advice still shows on their own card, framed as
 * something to watch — see `RecommendedActions`.
 *
 * ⚠️ IT FOLLOWS THE FILTER, like everything else on this tab. It is built from the same rows the
 * list below renders, so a board describing the company above a list showing one cluster cannot
 * happen.
 */
import { useState } from 'react';
import type { RadarPosition } from '../../../types/taBottleneck';
import type { RoleEvaluation } from '../../../utils/bottleneckRadar';
import type { RadarAction } from '../../../utils/radarActions';
import { Card, SectionHead } from '../rolePrioritisation/prioritisationUi';
import { ActionList } from './RecommendedActions';

export interface BoardRow {
  position: RadarPosition;
  evaluation: RoleEvaluation;
  actions: RadarAction[];
}

const SEVERITY_RANK = { red: 0, yellow: 1, green: 2 } as const;

/**
 * Roles with something to do, worst first. Ramping roles and roles whose only action is
 * "on track" are dropped — a board is a list of work, and "nothing to do" is not work.
 */
export function boardRows(
  rows: { position: RadarPosition; evaluation: RoleEvaluation; actions: RadarAction[] }[]
): BoardRow[] {
  return rows
    .filter((row) => !row.evaluation.ramp)
    .map((row) => ({ ...row, actions: row.actions.filter((a) => a.severity !== 'green') }))
    .filter((row) => row.actions.length > 0)
    .sort(
      (a, b) =>
        SEVERITY_RANK[a.actions[0].severity] - SEVERITY_RANK[b.actions[0].severity] ||
        b.position.openHires - a.position.openHires ||
        a.position.title.localeCompare(b.position.title)
    );
}

/** How many roles the board shows before it asks to be expanded. */
const COLLAPSED_ROWS = 5;

export function ActionBoard({ rows }: { rows: BoardRow[] }) {
  const [showAll, setShowAll] = useState(false);
  if (!rows.length) return null;

  const visible = showAll ? rows : rows.slice(0, COLLAPSED_ROWS);
  const hidden = rows.length - visible.length;

  return (
    <section>
      <SectionHead
        number="02"
        title="What to do this week"
        aside={`${rows.length} ${rows.length === 1 ? 'role needs' : 'roles need'} a decision`}
      />
      <Card className="p-4">
        <p className="mb-3 text-[11px] text-steel">
          Roles past the ramp-up phase, worst first. A role still settling keeps its advice on its
          own card.
        </p>
        <div className="flex flex-col gap-3">
          {visible.map(({ position, actions }) => (
            <div key={position.postingId} className="flex flex-col gap-1.5 sm:flex-row sm:gap-3">
              <div className="w-full shrink-0 pt-px text-[12.5px] font-semibold text-black sm:w-48">
                {position.title}
              </div>
              <div className="flex-1">
                <ActionList actions={actions} />
              </div>
            </div>
          ))}
        </div>
        {hidden > 0 || showAll ? (
          <button
            type="button"
            onClick={() => setShowAll((v) => !v)}
            className="mt-3 text-[12px] font-medium text-trustedBlue underline underline-offset-2"
          >
            {showAll ? 'Show fewer' : `Show ${hidden} more`}
          </button>
        ) : null}
      </Card>
    </section>
  );
}
